import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { customAlphabet } from 'nanoid';
import { NGXLogger } from 'ngx-logger';
import { map, Observable, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppExceptionModel } from '../models/app-exception.model';
import {
  AlertType,
  DlgConfirmComponent,
} from '../views/components/dialogs/dlg-confirm/dlg-confirm.component';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private dialog: MatDialog = inject(MatDialog);
  private logger: NGXLogger = inject(NGXLogger);

  static getRandomColor(): string {
    const r = Math.floor(Math.random() * 16777215).toString(16);
    // append 0's infront of r if it is less than 6 characters
    return '0'.repeat(6 - r.length) + r;
  }

  constructor(private authService: AuthService) {}

  static lowerFirstLetter(val: string) {
    return String(val).charAt(0).toLowerCase() + String(val).slice(1);
  }

  static getJSDate(date: any) {
    const ts = (date.seconds + date.nanos / 1000000000) * 1000;
    return new Date(ts);
  }

  async setHttpHeaders(
    idToken?: boolean,
    allowMethods?: string,
    apiKey?: string,
  ): Promise<HttpHeaders> {
    let headers = new HttpHeaders().set(
      'x-api-key',
      apiKey ?? environment['AWS_API_KEY']!,
    );
    if (idToken) {
      const token = await this.authService.getToken();
      headers = headers.set('AuthorizationToken', `Bearer ${token}`);
    }
    return headers;
  }

  async setAxiosHeaders(
    idToken?: boolean,
    allowMethods?: string,
    apiKey?: string,
  ): Promise<any> {
    let baseHeaders: any = {
      'x-api-key': apiKey ?? environment['AWS_API_KEY']!,
    };
    if (idToken) {
      const token = await this.authService.getToken();
      baseHeaders = {
        ...baseHeaders,
        AuthorizationToken: `Bearer ${token}`,
      };
    }
    return baseHeaders;
  }

  async setGroupHttpHeaders({
    idToken,
    allowMethods,
    apiKey,
    groupId,
  }: {
    idToken?: boolean;
    allowMethods?: string;
    apiKey?: string;
    groupId?: string;
  }): Promise<HttpHeaders> {
    let headers = new HttpHeaders().set(
      'x-api-key',
      apiKey ?? environment['AWS_API_KEY']!,
    );

    if (idToken) {
      const token = await this.authService.getToken();
      this.logger.debug(`User Auth Token: ${token}`);
      headers = headers.set('AuthorizationToken', `Bearer ${token}`);
    }

    if (groupId) {
      headers = headers.set('x-groupId', groupId);
    }

    return headers;
  }

  handleError(error: any): AppExceptionModel {
    let appException: AppExceptionModel = {
      code: 'unknown',
      title: 'Error Occurred',
      message: 'An error occurred. Please try again later',
    };
    if (error.code === 'permission-denied') {
      appException.code = 'permission-denied';
      appException.title = 'Permission Denied';
      appException.message =
        'You do not have permission to perform this action';
    } else {
      return error.message;
    }
    return appException;
  }

  static contentType(fileExtn: string): string {
    return fileExtn == 'pdf'
      ? 'application/pdf'
      : fileExtn == 'jpeg' || fileExtn == 'jpg'
        ? 'image/jpeg'
        : fileExtn == 'png'
          ? 'image/png'
          : 'application/json';
  }

  /**
   * Function to replace the domain of a URL with a custom user provided domain name
   */
  static replaceDomain(url: string, domain: string): string {
    const urlObj = new URL(url);
    const newUrl = urlObj.protocol + '//' + domain + urlObj.pathname;
    return newUrl;
  }

  static createInviteCode(): string {
    const alphabet = '123456789ABCDEFGHJKLMNPQRSTUVWXYZ';
    return customAlphabet(alphabet, 6)();
  }

  /**
   * Replaces the image URL to point to a resized version of the image.
   *
   * @param originalUrl
   * @param size
   * @returns
   */
  static getResizedImageUrl(
    originalUrl: string,
    size: 'thumb' | 'medium' | 'full' = 'full',
  ): string {
    if (size === 'full') return originalUrl;
    const decodedPath = decodeURIComponent(originalUrl);
    const fileName = decodedPath.split('/').pop()!;
    const prefix = size === 'thumb' ? 'thumb_' : 'medium_';
    return originalUrl.replace(fileName, `${prefix}${fileName}`);
  }

  onShowAlert(error: AppExceptionModel, showCancel?: boolean) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title ?? 'Error Occurred',
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
      showCancel: showCancel ?? true,
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }

  onRequestConfirmation({
    title,
    description,
    action,
    showType,
    buttonColor,
  }: {
    title: string;
    description: string;
    action: string;
    showType?: AlertType;
    buttonColor?: string;
  }): Observable<boolean> {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: title,
      description: description,
      action: action ?? 'Okay',
      buttonColor: buttonColor ?? 'bg-primary-500',
      showType: showType ?? AlertType.warning,
      showCancel: true,
    };
    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

    return dialogRef.afterClosed().pipe(
      take(1),
      map((result) => !!result),
    );
  }

  onDeleteConfirmation({
    title,
    description,
  }: {
    title: string;
    description: string;
  }): Observable<boolean> {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: title,
      description: description,
      action: 'Delete',
      buttonColor: 'bg-red-500',
      showType: AlertType.warning,
      showCancel: true,
    };

    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

    return dialogRef.afterClosed().pipe(
      take(1),
      map((result) => !!result),
    );
  }

  onShowConfirmation({
    title,
    description,
    action,
    alertType,
    buttonColor,
  }: {
    title: string;
    description: string;
    action: string;
    alertType: AlertType;
    buttonColor?: string;
  }) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: title,
      description: description,
      action: action ?? 'Okay',
      buttonColor: buttonColor ?? 'bg-primary-500',
      showType: alertType ?? AlertType.success,
      showCancel: false,
    };

    return this.dialog.open(DlgConfirmComponent, dialogConfig).afterClosed();
  }
}
